import React, {useEffect, useRef, useState} from 'react';
import {Box, Chip, FormControl, TextField} from '@mui/material';
import {styled} from '@mui/material/styles';
import {
  Body,
  Button,
  Icon,
  Modal,
  designSystemToken,
} from '@lightricks/react-design-system';
import {SCREEN_NAMES} from '@/lib/analytics/analyticsConstants';
import raiseFlashMessage, {
  raiseFlashMessageError,
} from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import useTrackSearchButtonEvent from '@/hooks/analytics/use-track-search-button-event';
import useRecruitToCrm from '@/hooks/mutations/creators/use-recruit-to-crm/useRecruitToCrm';
import parseEmails, {SEPARATOR_REGEX} from './parseEmails';

interface RecruitByEmailModalProps {
  open: boolean;
  agencyId: string;
  onClose: () => void;
}

const StyledModalContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
});

const StyledChipArea = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  maxHeight: '160px',
  overflowY: 'auto',
  padding: '8px',
  marginTop: '12px',
});

const StyledTextField = styled(TextField)({
  '& .MuiFilledInput-root': {
    'borderRadius': '12px',
    '&:before': {display: 'none'},
    '&:after': {display: 'none'},
  },
});

const StyledButtonRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  marginTop: '8px',
});

function RecruitByEmailModal({
  open,
  agencyId,
  onClose,
}: RecruitByEmailModalProps) {
  const trackButtonEvent = useTrackSearchButtonEvent();
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const chipAreaRef = useRef<HTMLDivElement>(null);

  const recruitToCrmMutation = useRecruitToCrm();

  useEffect(() => {
    if (open) {
      setEmails([]);
      setInputValue('');
    }
  }, [open]);

  useEffect(() => {
    if (chipAreaRef.current) {
      chipAreaRef.current.scrollTop = chipAreaRef.current.scrollHeight;
    }
  }, [emails]);

  const commitEmails = (input: string): boolean => {
    const newEmails = parseEmails(input);
    if (newEmails.length > 0) {
      setEmails((prev) => [...new Set([...prev, ...newEmails])]);
      setInputValue('');
      return true;
    }
    return false;
  };

  const handleDeleteEmail = (emailToDelete: string) => {
    setEmails((prev) => prev.filter((e) => e !== emailToDelete));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (SEPARATOR_REGEX.test(newValue)) {
      if (!commitEmails(newValue)) {
        setInputValue('');
      }
    } else {
      setInputValue(newValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === ',' ||
      event.key === ';'
    ) {
      event.preventDefault();
      commitEmails(inputValue);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const text = event.clipboardData.getData('text');
    if (commitEmails(text)) {
      event.preventDefault();
    }
  };

  const handleBlur = () => {
    if (!commitEmails(inputValue)) {
      setInputValue('');
    }
  };

  const handleSubmit = () => {
    trackButtonEvent.pressed({
      button_name: 'submit_recruit_by_email',
      screen_name: SCREEN_NAMES.RECRUIT_CRM,
      source: SCREEN_NAMES.RECRUIT_CRM,
    });

    recruitToCrmMutation.mutate(
      {agencyId, emails},
      {
        onSuccess: () => {
          raiseFlashMessage({
            status: 'success',
            mode: 'light',
            message: translate('Creators added to CRM'),
          });
          onClose();
        },
        onError: () => {
          raiseFlashMessageError({
            title: translate('Failed to add creators to CRM'),
            subtitle: translate('Please try again or come back later'),
          });
        },
      }
    );
  };

  const {isLoading} = recruitToCrmMutation;

  return (
    <Modal
      open={open}
      handleClose={isLoading ? undefined : onClose}
      size="large"
      header={translate('Recruit by Email')}
    >
      <StyledModalContent>
        <FormControl fullWidth>
          <StyledTextField
            variant="filled"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            placeholder={translate('Enter email addresses...')}
            inputProps={{onPaste: handlePaste}}
            fullWidth
          />
          {emails.length > 0 && (
            <StyledChipArea ref={chipAreaRef}>
              {emails.map((email) => (
                <Chip
                  key={email}
                  variant="outlined"
                  label={email}
                  onDelete={() => handleDeleteEmail(email)}
                  deleteIcon={
                    <Box sx={{display: 'flex'}}>
                      <Icon
                        size="medium"
                        appearance="neutral"
                        name="Actions-Close-Small"
                      />
                    </Box>
                  }
                />
              ))}
            </StyledChipArea>
          )}
        </FormControl>

        {emails.length > 0 && (
          <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {translate('email added', {count: emails.length})}
          </Body>
        )}

        <StyledButtonRow>
          <Button
            mode="tinted"
            appearance="neutral"
            size="medium"
            onClick={onClose}
            disabled={isLoading}
          >
            {translate('Cancel')}
          </Button>
          <Button
            mode="filled"
            appearance="neutral"
            size="medium"
            onClick={handleSubmit}
            disabled={emails.length === 0 || isLoading}
          >
            {isLoading ? translate('Adding...') : translate('Add to CRM')}
          </Button>
        </StyledButtonRow>
      </StyledModalContent>
    </Modal>
  );
}

export default RecruitByEmailModal;
